
export type DisposerFn = () => void;

type OmitNever<T extends Record<string, unknown>> = {
  [K in keyof T as T[K] extends never ? never : K]: T[K];
};
export type SharedProperties<A, B> = OmitNever<Pick<A & B, keyof A & keyof B>>;

export type Constructor<T = any> = new (...args: any) => T;

export type Path<T, P extends keyof T, P2 extends keyof T[P], P3 extends keyof T[P][P2]> = P | [] | [P] | [P, P2] | [P, P2, P3];

export type ObjectType<T> = { [K in keyof T]: T[K] };
export type NoInfer<T> = T & ObjectType<T>; // https://github.com/microsoft/TypeScript/issues/14829

export type Optional<T> = T | undefined

export type Primitive =
  | null
  | undefined
  | string
  | number
  | boolean
  | symbol
  | bigint;

export type Prim =
  | null
  | undefined
  | string
  | number
  | boolean
  | bigint;


export type PrimitiveType = string | number | boolean;

export type ValueOrCallback<T extends (object | Primitive)> = T | (() => T);
export function getValueOrCallback<T extends (object | Primitive)>(v: ValueOrCallback<T>): T {
  return typeof v === "function" ? v() : v;
}

export type ValueOrFunction<T extends (object | Primitive), P extends any[]> = T | ((...params: P) => T);
export function getValueOrFunction<T extends (object | Primitive), P extends any[]>(v: ValueOrFunction<T, P>, ...params: P): T {
  const ret = typeof v === "function" ? v(...params) : v;
  return ret;
}

/*
export type AssetItem = IVideoAsset | IImageAsset;
type ttt = GetTaggedType<AssetItem, { type: "image"}>;
}*/
export type GetTaggedType<Union, TagObject> = Union extends (Union & TagObject) ? Union : never;

export type PropertyNamesWithType<T, Type> = Exclude<{ [P in keyof T]: T[P] extends Type ? P : never }[keyof T], undefined>;
export type PropertiesWithType<T, Other> = {
  [P in PropertyNamesWithType<T, Other>]: Other;
};

// can be used to expand a type
export type Id<T extends object> = {} & { [P in keyof T]: T[P] };

/* ensures type for a certain created object
   typed<LanguageType>("de_AT");
   or
   typed<Item>({ type: "bla" }});
*/
export function typed<T>(state: T): T {
  return state;
}

export function inferRecordOld<V>(): <T extends Record<string, V>>(obj: T) => T {
  return (obj) => obj;
}

export function inferRecord<V>(): <T extends Record<string, V>>(obj: T) => Record<keyof T, V> {
  return (obj) => obj;
}

export type ObjectKeys<T> = (keyof T)[]
// T extends object ? (keyof T extends number ? `${keyof T}` : keyof T)[] :
// T extends number ? [] :
// T extends Array<any> | string ? string[] :
// never;
/**
 * get keys for an object
 */
export function getKeys<T extends object>(o: T | undefined): ObjectKeys<T> {
  if (!o)
    return [] as ObjectKeys<T>;
  return Object.keys(o) as ObjectKeys<T>;
}

export function inverseRecord<
  R extends Record<any, any>,
  KN extends (string | number | symbol),
  KO = R extends Record<infer Key, any> ? Key : never,
  V = R extends Record<any, infer Value> ? Value : never
>(a: R, fnMap: (item: V) => KN): PartialRecord<KN, KO> {
  const keys = getKeys(a);
  const ret: PartialRecord<KN, KO> = {} as any;
  for (const k of keys) {
    const val: KN = fnMap(a[k]);
    ret[val] = k as any;
  }
  return ret;
}


export function mapRecordToArray<T extends Record<string, any>, To, From = T extends Record<string, infer X> ? X : never>(obj: T, mapper: (from: From, id: keyof T, index: number) => To): To[] {
  const result: To[] = [];
  let index = 0;
  for (const k of getKeys(obj))
    result.push(mapper(obj[k], k, index++));
  return result
}

export function mapRecord<T extends Record<string, any>, To, From = T extends Record<string, infer X> ? X : never>(obj: T, mapper: (from: From, id: keyof T, index: number) => To): Record<keyof T, To> {
  const result: Record<any, any> = {}
  let index = 0;
  for (const k of getKeys(obj))
    result[k] = mapper(obj[k], k, index++)
  return result
}

export function filterRecord<T extends Record<string, any>, From = T extends Record<string, infer X> ? X : never>(obj: T, filterfn: (from: From, id: keyof T, index: number) => boolean): T {
  const result: Record<any, any> = {}
  let index = 0;
  for (const k of getKeys(obj)) {
    const from = obj[k];
    if (filterfn(from, k, index))
      result[k] = from;
    index++;
  }
  return result
}

export function getValues<T>(o: Record<string, T> | undefined): T[] {
  if (!o)
    return [];
  return Object.values(o);
}

export function isWritable<T extends object>(obj: T, key: keyof T): boolean {
  const desc = Object.getOwnPropertyDescriptor(obj, key) || {}
  return Boolean(desc.writable)
}

export function mapCount<U>(count: number | undefined, fn: (index: number) => U): U[] {
  const ret: U[] = [];
  if (count) {
    for (let i = 0; i < count; i++)
      ret.push(fn(i));
  }
  return ret;
}

export function mapArrayToRecord<T>(items: readonly T[] | undefined, getId: (item: T) => string): Record<string, T>;
export function mapArrayToRecord<T, T2>(items: readonly T[] | undefined, getId: (item: T) => string, getVal: (item: T) => T2): Record<string, T2>;
export function mapArrayToRecord<T, T2>(items: readonly T[] | undefined, getId: (item: T) => string, getVal?: (item: T) => T2): Record<string, T2> {
  const map: Record<string, T2> = {};
  if (!items) return map;

  for (const a of items) {
    const id = getId(a);
    map[id] = getVal ? getVal(a) : a as any;
  }
  return map;
}

export function mapArrayToRecord2<T, T2>(items: readonly T[] | undefined, get: (item: T) => { id: string, val: T2 }): Record<string, T2> {
  const map: Record<string, T2> = {};
  if (!items) return map;

  for (const a of items) {
    const item = get(a);
    map[item.id] = item.val;
  }
  return map;
}


export function mapToArray<T>(articles: Record<string, T> | undefined): T[];
export function mapToArray<T, R>(articles: Record<string, T> | undefined, fn: (id: string, item: T) => R): R[];
export function mapToArray<T, R>(articles: Record<string, T> | undefined, fn?: (id: string, item: T) => R): R[] {
  if (!articles) return [];
  if (fn) {
    return Object.keys(articles).map((key) => fn(key, articles[key]));
  }
  return Object.values(articles) as unknown[] as R[];
}

export function remap<T>(articles: Record<string, T>, fn: (id: string, item: T) => string): Record<string, T> {
  const ret: Record<string, T> = {};
  const keys = Object.keys(articles);
  for (const k of keys) {
    const newId = fn(k, articles[k]);
    if (ret[newId]) {
      throw new Error("Id already exists! " + newId)
    }
    ret[newId] = articles[k]
  }
  return ret;
}

export function setOptional<T extends object, K extends keyof T>(obj: T | undefined | null, key: K, val: T[K]): void {
  if (obj)
    obj[key] = val;
}

/**
 * Tuple to union
 * export const LanguageTypeValues = ["de_AT", "en_GB", "fr_FR", "it_IT", "es_ES"] as const;
 * type TupleToUnion<T extends readonly string[], U = never> = T[number] | U;
 * export type LanguageType = TupleToUnion<typeof LanguageTypeValues>;
 * */
export type TupleToUnion<T extends readonly any[], U = never> = T[number] | U;
/**
 *
 */
export type TupleTag<T, TagName extends string> = {
  [P in keyof T]: T[P] extends Record<TagName, string> ? T[P][TagName] : never;
};

// Just some tests
/*type taggedMap = {
  image: { x: string; y: number };
  pdf: number;
  video: string;
  newsletter: any;
};
type tt = AssetItem["type"];
type tt3<T> = { [Key in keyof T]: T[Key] };
type tt5<M> = keyof taggedMap;
type tt7 = tt5<taggedMap>;
type tt4 = tt3<taggedMap>;

type t<T> = T extends keyof taggedMap ? (taggedMap[T] extends number ? true : false) : never;
// type AND<A, B> = A extends true ? B extends true ? true : false : false;
// type ta<T> = AND<T extends keyof taggedMap ? true : false, taggedMap[T] extends number ? true : false>
type t2 = t<"test">;
type t3 = t<"image">;
type t4 = t<"pdf">;*/
// Just some tests

// doesn't break discriminated unions...
// type OmitBetter<T, K extends keyof any> = T extends any ? Pick<T, Exclude<keyof T, K>> : never;
export type OmitStrict<T, K extends keyof T> = T extends any ? Pick<T, Exclude<keyof T, K>> : never;

export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

export type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;
export type DiscriminatedUnionToIntersection<U, DISCRIMINATOR extends keyof U> = UnionToIntersection<OmitStrict<U, DISCRIMINATOR>>;

// Flattens two union types into a single type with optional values
// i.e. FlattenUnion<{ a: number, c: number } | { b: string, c: number }> = { a?: number, b?: string, c: number }
export type FlattenUnion<T> = {
  [K in keyof UnionToIntersection<T>]: K extends keyof T ? (T[K] extends any[] ? T[K] : T[K] extends object ? FlattenUnion<T[K]> : T[K]) : UnionToIntersection<T>[K] | undefined;
};

// type Merged = UnionToIntersection<OmitStrict<OrderSailInputModel, "sunSailType" | "poleCount">>

/**
 * Returns an interface stripped of all keys that don't resolve to U, defaulting
 * to a non-strict comparison of T[key] extends U. Setting B to true performs
 * a strict type comparison of T[key] extends U & U extends T[key]
 */
export type KeysOfType<T, U, B = false> = {
  [P in keyof T]: B extends true ? (T[P] extends U ? (U extends T[P] ? P : never) : never) : T[P] extends U ? P : never;
}[keyof T];

export type PickByType2<T, U, B = false> = Pick<T, KeysOfType<T, U, B>>;
export type PickByType<T, Value> = {
  [P in keyof T as T[P] extends Value | undefined ? P : never]: T[P]
}

export type Head<T extends any[]> = T extends [any, ...any[]] ? T[0] : never;

export type Tail<T extends any[]> = ((...t: T) => any) extends (_: any, ...tail: infer TT) => any ? TT : [];

export type HasTail<T extends any[]> = T extends [] | [any] ? false : true;

// wrap an objects properties into promises
export type PromiseWrap<T extends object> = {
  [K in keyof T]: Promise<T[K]>;
}


export function pick<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K> {
  if (!obj)
    return obj;
  const ret: any = {};
  keys.forEach(key => {
    if (key in obj) {
      ret[key] = obj[key];
    }
  })
  return ret;
}

export function omit<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K> {
  const ret: any = {};
  Object.keys(obj).forEach((key) => {
    if (keys.indexOf(key as K) === -1)
      ret[key] = obj[key as K];
  })
  return ret;
}

export type MakeOptional<Type, Key extends keyof Type> = Omit<Type, Key> & Partial<Pick<Type, Key>>;

export const isNullish = <T>(argument: T | undefined | null): argument is undefined | null =>
  argument === null || argument === undefined;

export const nonNullish = <T>(argument: T | undefined | null): argument is NonNullable<T> =>
  argument !== null && argument !== undefined;


// https://medium.com/stackanatomy/forever-functional-complex-typing-in-typescript-part-2-ad5bdb274d6e
type FullCurry<P, R> = P extends []
  ? R
  : P extends [infer H]
  ? (arg: H) => R // only 1 arg
  : P extends [infer H, ...infer T] // 2 or more args
  ? (arg: H) => FullCurry<[...T], R>
  : never;

export function curry<P extends any[], R>(fn: (...args: P) => R): FullCurry<P, R>;
export function curry(fn: AnyFunction): any {
  return fn.length === 0
    ? fn()
    : (x: any) => curry(fn.bind(null, x));
}

export type IfNever<T, TypeIfNever, TypeIfNotNever> = [T] extends [never] ? TypeIfNever : TypeIfNotNever
export type FallbackIfNever<T, FallbackTo> = IfNever<T, FallbackTo, T>
export type IfUnknown<T, TypeIfUnknown, TypeIfNotUnknown> = unknown extends T // `T` can be `unknown` or `any`
  ? [T] extends [null] // `any` can be `null`, but `unknown` can't be
  ? TypeIfNotUnknown
  : TypeIfUnknown
  : TypeIfNotUnknown
export type FallbackIfUnknown<T, FallbackTo> = IfUnknown<T, FallbackTo, T>

export type AnyFunction = (...args: any) => any
export type ReplaceReturnType<T extends (...a: any) => any, TNewReturn> = (...a: Parameters<T>) => TNewReturn;